import React, { useState, useEffect } from 'react';
import { getWebAppUrl } from '../../../config/runtimeConfig';
import { Box, Avatar, Chip } from '@mui/material';
import { getAllUsersByIssuerId, updateUser } from '../../../services/apiservice';
import { useAuth } from '../../../contexts/authContext';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import UserEditModal from './UserEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

const NoData = () => <span style={{ color: '#ccc' }}>No data</span>;

function UserList() {
  const { user } = useAuth();
  const issuerId = user.IssuerID;
  const { data: fetchedUsers, loading } = useApiData(() => getAllUsersByIssuerId(issuerId).then((res) => res.data), [issuerId]);
  const [users, setUsers] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modelOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const webAppUrl = `${getWebAppUrl()}/users/`;

  useEffect(() => {
    if (fetchedUsers) {
      setUsers(fetchedUsers);
    }
  }, [fetchedUsers]);

  const handleEdit = (selected) => {
    setSelectedUser(selected);
    setModalOpen(true);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  const handleSave = async (updatedUser) => {
    try {
      const response = await updateUser(updatedUser);
      if (response.success) {
        setUsers((prevUsers) =>
          prevUsers.map((u) =>
            u.UserID === updatedUser.UserID ? { ...u, ...updatedUser } : u
          )
        );
        setSnackbarMessage('User updated successfully!');
        setSnackbarSeverity('success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'UserID', label: 'User ID', width: 100 },
    {
      key: 'UserName',
      label: 'User Name',
      width: 200,
      render: (u) => (
        <Box display="flex" alignItems="center">
          <Avatar src={u.ProfileImageUrl} sx={{ width: 40, height: 40, marginRight: 2 }} />
          <span style={{ color: '#000' }}>
            {u.UserFirstName || u.UserLastName
              ? (`${u.UserFirstName || ''} ${u.UserLastName || ''}`.trim() || 'No data')
              : <NoData />}
          </span>
        </Box>
      ),
    },
    { key: 'UserEmail', label: 'Email', width: 250 },
    { key: 'UserPhoneNumber', label: 'Phone Number', width: 150 },
    { key: 'UserCompanyName', label: 'Company Name', width: 200 },
    {
      key: 'badgeUrl',
      label: 'Badge URL',
      width: 120,
      render: (u) => (
        u.ProfileID ? (
          <span><a href={`${webAppUrl}${u.ProfileID}`} target="_blank" rel="noopener noreferrer"><b>Click Here</b></a></span>
        ) : (
          <NoData />
        )
      ),
    },
    {
      key: 'UserIsVerified',
      label: 'Verified',
      width: 120,
      render: (u) => (
        u.UserIsVerified === true ? (
          <Chip label="Verified" color="primary" size="small" sx={{ color: 'white', backgroundColor: 'primary.main' }} />
        ) : (
          <Chip label="Unverified" color="warning" size="small" sx={{ color: 'white', backgroundColor: 'warning.main' }} />
        )
      ),
    },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Users List"
        columns={columns}
        rows={users}
        loading={loading}
        searchKeys={[(u) => `${u.UserFirstName || ''} ${u.UserLastName || ''}`, 'UserEmail', 'UserPhoneNumber', 'UserJobTitle', 'UserCompanyName']}
        rowKey="UserID"
        onEdit={handleEdit}
      />
      <UserEditModal
        open={modelOpen}
        onClose={handleModalClose}
        user={selectedUser}
        onSave={handleSave}
      />
    </>
  );
}

export default UserList;
